/**
 * @Author: Jhovanny Fragoso Márquez
 * @JobTitle: Frontend Developer
 * @Email: jfragoso@ibl.mx
 */

// Import necessary constants, components, and helpers
import { FILE_TYPE } from '../../../Helpers/fileType';
import { permissionObj } from '../../../Helpers/permission';
import routes from '../../../Routes/routes';

// Custom components used in the form
import { CustomTextField } from '../../../components/CustomTextField';
import DivContainerBody from '../../../components/DivContainerBody';
import DividerWithText from '../../../components/DividerWithText';
import HeadTittle from '../../../components/HeadTittle';
import ZoneLeaflet from '../../../components/ZoneLeaflet';
import ButtonAction from '../../../components/buttons/ButtonAction';
import ButtonSubAction from '../../../components/buttons/ButtonSubAction';
import { EditIcon} from '../../../components/buttons/IconButtons';

// Custom hook containing business logic for the component
import UpdateDataApp from './Functionality/UpdateDataApp';
import {ACTION_MODEL} from '../../../Helpers/ActionModel';
import CustomPhotoPicker from '../../../components/PhotoPicker/CustomPhotoPicker';
import React from 'react';

// Component for updating company data
const UpdateData = () => {
    // Destructuring state and handlers from the custom hook
    const {
        handleSubmit,
        handleChange,
        companyData,
        showMap,
        setShowMap,
        t, // translation function
        DEV, // development mode flag
        permission, // user permission list
        emptyFields,
        photoPicker,
        setPhotoPicker
    } = UpdateDataApp();



    return (
        <>
            {/* Page header with title and edit button */}
            <HeadTittle name={t('company_profile')} link={routes.company.update} hidden={true}>
                {(DEV || permission?.includes(permissionObj?.WEB_COMPANY_UPDATE)) && (
                    <ButtonAction
                        id='btn_up_update'
                        name='update'
                        Icon={EditIcon}
                        className='btn-icon-edit'
                        onClick={() => handleSubmit()}
                    />
                )}
            </HeadTittle>

            {/* Main container */}
            <DivContainerBody>
                <div className='row'>
                    {/* Section title */}
                    <div className='col-12 mb-4'>
                        <DividerWithText title={t('data')} />
                    </div>

                    {/* Company name field */}
                    <div className='col-4'>
                        <CustomTextField
                            id='txt_up_name'
                            length={128}
                            name='name'
                            value={companyData.name}
                            trans={'company_name'}
                            type='text'
                            onChange={handleChange}
                            disabled={!DEV && !(permission?.includes(permissionObj?.WEB_COMPANY_UPDATE))}
                            required
                            emptyFields={emptyFields}
                        />
                    </div>

                    {/* Company acronym field */}
                    <div className='col-4'>
                        <CustomTextField
                            id='txt_up_acronym'
                            length={128}
                            name='acronym'
                            value={companyData.acronym}
                            trans={'acronym'}
                            type='text'
                            onChange={handleChange}
                            disabled={!DEV && !(permission?.includes(permissionObj?.WEB_COMPANY_UPDATE))}
                            required
                            emptyFields={emptyFields}
                        />
                    </div>

                    {/* Address field (read-only) */}
                    <div className='col-4'>
                        <CustomTextField
                            id='txt_up_address'
                            length={128}
                            name='address'
                            value={companyData?.address}
                            trans={'address'}
                            type='text'
                            disabled={true}
                            required
                            emptyFields={emptyFields}
                        />
                    </div>

                    {/* Button to toggle map display */}
                    {!(!DEV && !permission?.includes(permissionObj?.WEB_COMPANY_UPDATE)) &&
                        <div className='col-12 px-0 d-flex justify-content-end'>
                            <ButtonSubAction
                                id='btn_up_click_to_map'
                                name={showMap ? 'click_to_close_map' : 'click_to_show_map'}
                                onClick={() => setShowMap(!showMap)}
                            />
                        </div>
                    }
                </div>

                {/* Map and image upload section */}
                <div className='dragdrop_row'>
                    <div className='row'>
                        <div className='col-12 px-0'>
                            {showMap && !(!DEV && !permission?.includes(permissionObj?.WEB_COMPANY_UPDATE)) && (
                                <ZoneLeaflet
                                    latlng={[companyData?.latitud, companyData?.longitud]}
                                    setLatitude={(lat) =>
                                        handleChange({ target: { name: 'latitud', value: lat } })
                                    }
                                    setLongitude={(lng) =>
                                        handleChange({ target: { name: 'longitud', value: lng } })
                                    }
                                    setAddress={(address) =>
                                        handleChange({ target: { name: 'address', value: address } })
                                    }
                                />
                            )}
                        </div>

                        {/* Logo upload section */}
                        <div className={`col-12 ${showMap && 'mt-4'}`}>
                            <DividerWithText title={t('image_logo')} />
                        </div>

                        <div className='col-12'>
                            <CustomPhotoPicker
                                id='fp_aei_create_photo_file'
                                photoPicker={photoPicker}
                                setPhotoPicker={setPhotoPicker}
                                actionModel={ACTION_MODEL.CREATE}
                                accept={{ ...FILE_TYPE.IMAGE}}
                                disabled={!DEV && !permission?.includes(permissionObj?.WEB_COMPANY_UPDATE)}
                                classWith={'w-400px'}
                            />
                        </div>

                        {/* Company mission and vision */}
                        <div className='col-12 my-4'>
                            <DividerWithText title={t('our_values')} />
                        </div>

                        <div className='col-6'>
                            <CustomTextField
                                id='txt_up_mission'
                                length={128}
                                name='mission'
                                value={companyData.mission}
                                trans={'mission'}
                                type='text'
                                onChange={handleChange}
                                disabled={!DEV && !permission?.includes(permissionObj?.WEB_COMPANY_UPDATE)}
                                required
                                emptyFields={emptyFields}
                            />
                        </div>

                        <div className='col-6'>
                            <CustomTextField
                                id='txt_up_vision'
                                length={128}
                                name='vision'
                                value={companyData.vision}
                                trans={'vision'}
                                type='text'
                                onChange={handleChange}
                                disabled={!DEV && !permission?.includes(permissionObj?.WEB_COMPANY_UPDATE)}
                                required
                                emptyFields={emptyFields}
                            />
                        </div>

                        {/* Company introduction */}
                        <div className='col-12'>
                            <CustomTextField
                                id='txt_up_introduction'
                                length={128}
                                name='introduction'
                                value={companyData.introduction}
                                trans={'introduction'}
                                type='text'
                                onChange={handleChange}
                                disabled={!DEV && !permission?.includes(permissionObj?.WEB_COMPANY_UPDATE)}
                                multiline={true}
                                required
                                emptyFields={emptyFields}
                            />
                        </div>
                    </div>
                </div>
            </DivContainerBody>
        </>
    );
};

export default UpdateData;
